import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { Button } from '@/components/Button';
import { Radio } from '@/components/Radio';
import { Textarea } from '@/components/Textarea';
import { useMicromodal } from '@/hooks/useMicromodal';
import useFetchIam from '@/features/agent/api/useFetchIam';
import type { AgentApiCandidateChatCommentsRecord } from '@/types/serializers';
import { showErrorToast, showSuccessToast } from '../../job_change_processes/lib/toast';
import useFetchCandidateChatComments from '../api/useFetchCandidateChatComments';
import usePostCandidateChatComments from '../api/usePostCandidateChatComments';
import useDeleteCandidateChatComment from '../api/useDeleteCandidateChatComment';
import useDeleteCandidateInterviewLog from '../api/useDeleteCandidateInterviewLog';
import useFetchInterviewCustomFields from '../api/useFetchInterviewCustomFields';
import { TIMELINE_STATUS_ITEMS, type TimelineFilter } from '../constants';
import {
  candidatesChatCommentsSchema,
  type CandidatesChatCommentsSchema,
} from '../schemas/candidatesChatCommentsSchema';
import CandidatesTimelineItem from './CandidatesTimelineItem';
import CandidatesInterviewDropdown from './CandidatesInterviewDropdown';
import CandidatesInterviewTranscriptDialog from './CandidatesInterviewTranscriptDialog';
import CandidatesInterviewInfoUpdateModal from './CandidatesInterviewInfoUpdateModal';
import CandidatesTimelineDeleteModal from './CandidatesTimelineDeleteModal';

const INPUT_MIN_HEIGHT = 99;
const INPUT_MAX_HEIGHT = 158;

const resizeInput = (el: HTMLTextAreaElement) => {
  el.style.height = 'auto';
  el.style.height = `${Math.min(Math.max(el.scrollHeight, INPUT_MIN_HEIGHT), INPUT_MAX_HEIGHT)}px`;
  el.style.overflowY = el.scrollHeight > INPUT_MAX_HEIGHT ? 'auto' : 'hidden';
};

type Props = {
  candidateId: number;
  filter: TimelineFilter;
  onFilterChange: (filter: TimelineFilter) => void;
};

export default function CandidatesTimelineCard({ candidateId, filter, onFilterChange }: Props) {
  const navigate = useNavigate();
  const [selectedInterviewLogId, setSelectedInterviewLogId] = useState<number | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<AgentApiCandidateChatCommentsRecord | null>(
    null
  );
  const [isTranscriptDialogOpen, setIsTranscriptDialogOpen] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const { data: iam } = useFetchIam();
  const currentUserId = iam?.current_user?.id;
  const { data, hasNextPage, isFetchingNextPage, fetchNextPage, refetch } =
    useFetchCandidateChatComments({
      id: candidateId,
      sourceType: filter === 'system' ? 'system' : undefined,
    });
  const { data: customFields } = useFetchInterviewCustomFields();
  const { mutateAsync: postChatComment } = usePostCandidateChatComments();
  const { mutateAsync: deleteChatComment, isPending: isDeletingComment } =
    useDeleteCandidateChatComment();
  const { mutateAsync: deleteInterviewLog, isPending: isDeletingInterviewLog } =
    useDeleteCandidateInterviewLog();
  const {
    open: interviewInfoUpdateOpen,
    close: interviewInfoUpdateClose,
    id: interviewInfoUpdateId,
    isShow: isInterviewInfoUpdateShow,
  } = useMicromodal('interview-info-update-modal');
  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid, isSubmitting },
  } = useForm<CandidatesChatCommentsSchema>({
    resolver: zodResolver(candidatesChatCommentsSchema),
    mode: 'all',
    defaultValues: { body: '' },
  });

  const { ref: bodyRegisterRef, ...bodyField } = register('body', {
    onChange: (e) => resizeInput(e.target),
  });
  const deleteTargetInterviewLog = deleteTarget?.interview_log;

  const chatComments = useMemo(() => data?.pages.flatMap((page) => page.records) ?? [], [data]);

  const loaderRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      if (!node) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { threshold: 0.5 }
      );
      observerRef.current.observe(node);
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  const onSubmit: SubmitHandler<CandidatesChatCommentsSchema> = async ({ body }) => {
    try {
      await postChatComment({ candidateProfileId: candidateId, body });
      reset();
      if (textareaRef.current) resizeInput(textareaRef.current);
      await refetch();
    } catch (error) {
      showErrorToast('送信に失敗しました');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      if (deleteTargetInterviewLog) {
        await deleteInterviewLog({
          candidateProfileId: candidateId,
          interviewLogId: deleteTargetInterviewLog.id,
        });
      } else {
        await deleteChatComment({
          candidateProfileId: candidateId,
          chatCommentId: deleteTarget.id,
        });
      }
      setDeleteTarget(null);
      showSuccessToast(
        deleteTargetInterviewLog ? '面談情報を削除しました' : 'メッセージを削除しました'
      );
      await refetch();
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : '削除に失敗しました');
    }
  };

  const openInterviewLogEdit = (interviewLogId: number) => {
    setSelectedInterviewLogId(interviewLogId);
    interviewInfoUpdateOpen();
  };

  return (
    <div className="flex flex-col gap-xs">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-xs rounded-sm bg-white p-md"
      >
        <Textarea
          placeholder="メッセージを入力..."
          className="min-h-[99px] resize-none"
          ref={(el) => {
            bodyRegisterRef(el);
            textareaRef.current = el;
          }}
          {...bodyField}
        />
        <div className="flex items-center justify-between">
          <CandidatesInterviewDropdown
            variant="timeline"
            items={[
              {
                label: '手動で入力',
                onSelect: () =>
                  navigate({
                    to: '/agent/candidates/$candidateProfileId/interview_logs/new',
                    params: { candidateProfileId: String(candidateId) },
                  }),
              },
              { label: '文字起こしから入力', onSelect: () => setIsTranscriptDialogOpen(true) },
            ]}
          />
          <Button type="submit" color="primary" size="md" disabled={isSubmitting || !isValid}>
            送信
          </Button>
        </div>
      </form>
      <div className="flex h-[42px] items-center gap-md px-[5px]">
        {TIMELINE_STATUS_ITEMS.map((item) => (
          <Radio
            key={item.value}
            value={item.value}
            checked={filter === item.value}
            labelClassName="font-normal"
            onChange={() => onFilterChange(item.value)}
          >
            {item.label}
          </Radio>
        ))}
      </div>
      <div className="flex flex-col gap-xs">
        {chatComments.map((chatComment) => (
          <CandidatesTimelineItem
            key={chatComment.id}
            chatComment={chatComment}
            customFields={customFields?.records ?? []}
            canDelete={
              chatComment.source_type === 'user' &&
              !!currentUserId &&
              chatComment.author?.id === currentUserId
            }
            onEditInterviewLog={openInterviewLogEdit}
            onDelete={setDeleteTarget}
          />
        ))}
        {hasNextPage && <div ref={loaderRef} className="h-xxl w-full" />}
      </div>
      <CandidatesInterviewTranscriptDialog
        candidateProfileId={candidateId}
        isOpen={isTranscriptDialogOpen}
        onOpenChange={setIsTranscriptDialogOpen}
      />
      <CandidatesInterviewInfoUpdateModal
        close={interviewInfoUpdateClose}
        modal={{ isShow: isInterviewInfoUpdateShow, id: interviewInfoUpdateId }}
        candidateProfileId={candidateId}
        selectedInterviewLogId={selectedInterviewLogId}
        onSuccess={refetch}
        customFields={customFields?.records ?? []}
      />
      <CandidatesTimelineDeleteModal
        isOpen={deleteTarget !== null}
        title={
          deleteTargetInterviewLog
            ? `${deleteTargetInterviewLog.organization_user.name}が作成した面談情報`
            : `${deleteTarget?.author?.name ?? ''}のメッセージ`
        }
        message={
          deleteTargetInterviewLog ? '面談情報を削除しますか？' : 'メッセージを削除しますか？'
        }
        isPending={isDeletingComment || isDeletingInterviewLog}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
